import { KeyValue } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, inject, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { isSessionTimeoutKey, SessionTrackingService } from '@capital-access/common/auth';
import { CookieSettingsService } from '@capital-access/common/components';
import { getFeatureToggle } from '@capital-access/common/feature-toggle';
import { Log } from '@capital-access/common/logging';
import { getCurrentRouteAuthorized } from '@capital-access/common/router';
import { AppSettingsService } from '@capital-access/common/settings';
import {
  getAccountPreferences,
  getUserLoadStatus,
  REPORT_ACCESS_ONLY_PREFERENCE_KEY,
  REPORT_ACCESS_RESTRICTION_FEATURE_TOGGLE
} from '@capital-access/common/user';
import { LoadStatus, mapToBnyReportOnlyAccessRestriction } from '@capital-access/common/utils';
import { isIpad, isMobile, isTablet, MobileInnerErrorOptions } from '@capital-access/firefly/components';

@Component({
  selector: 'ca-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements AfterViewInit, OnDestroy {
  flow$?: Observable<'authorized' | 'anonymous'>;
  mobileInnerErrorOptions = MobileInnerErrorOptions;
  isLandscapeOrientation = false;

  shouldHideAiChatButton$: Observable<boolean>;

  private destroyRef = inject(DestroyRef);
  private orientationChangeHandler?: () => void;
  constructor(
    private store: Store,
    private appSettings: AppSettingsService,
    private sessionTrackingService: SessionTrackingService,
    private cookieSettingsService: CookieSettingsService
  ) {
    this.logAppInfo();
    this.cookieSettingsService.initializeOneTrust();

    const userStatus$ = this.store.select(getUserLoadStatus);
    const preferences$ = this.store.select(getAccountPreferences);
    const featureToggle$ = this.store.select(getFeatureToggle(REPORT_ACCESS_RESTRICTION_FEATURE_TOGGLE));

    this.shouldHideAiChatButton$ = combineLatest([userStatus$, preferences$, featureToggle$]).pipe(
      mapToBnyReportOnlyAccessRestriction(REPORT_ACCESS_ONLY_PREFERENCE_KEY)
    );
  }

  ngAfterViewInit() {
    this.flow$ = this.store.select(getCurrentRouteAuthorized).pipe(
      filter(x => x !== null),
      map(authorized => (authorized ? 'authorized' : 'anonymous'))
    );
    this.detectScreenOrientation();
    this.StartTrackingSession();
  }

  private logAppInfo() {
    const { version } = this.appSettings.getSettings<{ version: string }>();

    Log.info(`Application info: ${version}`);
  }

  private detectScreenOrientation() {
    if (isMobile() && !isTablet() && !isIpad()) {
      this.orientationChangeHandler = () =>
        (this.isLandscapeOrientation = screen.orientation.type.includes('landscape'));
      this.orientationChangeHandler();
      screen.orientation.addEventListener('change', this.orientationChangeHandler);
    }
  }

  private StartTrackingSession() {
    this.store
      .select(getAccountPreferences)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((preferences): preferences is KeyValue<string, string>[] => !!preferences),
        map(preferences => preferences.find(isSessionTimeoutKey)),
        tap(preferences => {
          if (preferences) {
            this.startUserActivity(preferences);
          } else {
            this.sessionTrackingService.startTracking(0);
          }
        })
      )
      .subscribe();
  }

  private startUserActivity(preference: KeyValue<string, string>): void {
    const timeout = Number(preference?.value);
    if (!isNaN(timeout) && timeout > 0) {
      this.sessionTrackingService.startTracking(timeout);
    } else {
      this.sessionTrackingService.startTracking(0);
    }
  }

  ngOnDestroy() {
    // Stop session tracking and clean up event listeners
    this.sessionTrackingService.stopTracking();

    // Remove screen orientation event listener
    if (this.orientationChangeHandler) {
      screen.orientation.removeEventListener('change', this.orientationChangeHandler);
    }
  }
}
